import React, { useEffect } from "react";
import { useMerchantModal } from "./hooks/useMerchantModal";
import { useMerchantTableHandlers } from "./hooks/useMerchantTableHandlers";
import { useMerchantFormSubmit } from "./hooks/useMerchantFormSubmit";
import { useMobileMoneyModal } from "./hooks/useMobileMoneyModal";
import AddMobileMoneyModal from "../MyCompany/components/modals/AddMobileMoneyModal";
import MerchantTableContent from "./components/MerchantTableContent";
import MerchantFilters from "./components/MerchantFilters";
import { MerchantInstitutionModal } from "./modals/MerchantModals";
import useMerchantInstitutionForm from "./hooks/useMerchantForm";

const ManageMerchantContent = React.memo(({
  merchants,
  loadingData,
  referenceDataHook,
}) => {
  const {
    referenceData: {
      economicSectors,
      genders,
      productTransactionCodes,
      productOwnerships,
      productCategories,
    },
    loadingStates: {
      economicSectorsLoading,
      gendersLoading,
      productTransactionCodesLoading,
      productOwnershipsLoading,
      productCategoriesLoading,
    },
    errorStates: {
      economicSectorsError: economicSectorsHasError,
      gendersError: gendersHasError,
      productTransactionCodesError: productTransactionCodesHasError,
      productOwnershipsError: productOwnershipsHasError,
      productCategoriesError: productCategoriesHasError,
    },
    getAllErrorItemNames,
    isAnyLoading: isReferenceDataLoading,
  } = referenceDataHook;

  const merchantForm = useMerchantInstitutionForm();
  const { setMode } = merchantForm;

  const {
    modalMode,
    selectedMerchant,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useMerchantModal({ onModalClose: merchantForm.resetToDefaults });

  useEffect(() => {
    if (modalMode) {
      setMode(modalMode);
    }
  }, [modalMode, setMode]);

  const {
    setSearchTerm,
    filteredMerchants,
    loadingIds,
    handleToggleStatus,
    noMerchants,
    noResults,
  } = useMerchantTableHandlers({ merchants });

  const errorItemNames = getAllErrorItemNames?.() || [];
  const disableAddMerchant = loadingData || isReferenceDataLoading || errorItemNames.length > 0;
  
  const addMerchantTitle = loadingData || isReferenceDataLoading
    ? "Loading merchant data..."
    : errorItemNames.length > 0
    ? `Cannot add merchant: failed to load ${errorItemNames.join(", ")}.`
    : undefined;

  const {
    handleSubmit,
    isSubmitting,
    merchantDetailsLoading,
    merchantDetailsError,
  } = useMerchantFormSubmit({
    merchantForm,
    modalMode,
    selectedMerchant,
    closeModal,
  });

  const {
    isOpen: mobileMoneyModalOpen,
    openModal: openMobileMoneyModal,
    closeModal: closeMobileMoneyModal,
    handleSubmit: handleMobileMoneySubmit,
    control: mobileFormControl,
    isCreating: creatingMerchantMobile,
    disableSubmit: disableMobileMoneySubmit,
  } = useMobileMoneyModal();

  return (
    <div className="flex flex-col min-h-full">
      <MerchantFilters
        onSearchChange={setSearchTerm}
        badgeCount={filteredMerchants.length}
        onAddMerchant={openCreateModal}
        disableAddMerchant={disableAddMerchant}
        addMerchantTitle={addMerchantTitle}
      />
      <div className="flex-1">
        <MerchantTableContent
          loadingData={loadingData}
          noMerchants={noMerchants}
          noResults={noResults}
          filteredMerchants={filteredMerchants}
          onEdit={openEditModal}
          onToggleStatus={handleToggleStatus}
          onMobileMoneyAdd={openMobileMoneyModal}
          loadingIds={loadingIds}
        />
      </div>
      <MerchantInstitutionModal
        opened={!!modalMode}
        onClose={closeModal}
        mode={modalMode}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        economicSectorsData={economicSectors}
        economicSectorsLoading={economicSectorsLoading}
        gendersData={genders}
        gendersLoading={gendersLoading}
        productTransactionCodesData={productTransactionCodes}
        productTransactionCodesLoading={productTransactionCodesLoading}
        productOwnershipData={productOwnerships}
        productOwnershipLoading={productOwnershipsLoading}
        productCategoriesData={productCategories}
        productCategoriesLoading={productCategoriesLoading}
        economicSectorsHasError={economicSectorsHasError}
        gendersHasError={gendersHasError}
        productTransactionCodesHasError={productTransactionCodesHasError}
        productOwnershipsHasError={productOwnershipsHasError}
        productCategoriesHasError={productCategoriesHasError}
        control={merchantForm.control}
        modeRef={merchantForm.modeRef}
        disableSubmit={
          (modalMode === "edit" && merchantDetailsLoading) ||
          isSubmitting
        }
        merchantDetailsLoading={merchantDetailsLoading}
        merchantDetailsError={merchantDetailsError}
      />
      <AddMobileMoneyModal
        opened={mobileMoneyModalOpen}
        onClose={closeMobileMoneyModal}
        onSubmit={handleMobileMoneySubmit}
        control={mobileFormControl}
        isSubmitting={creatingMerchantMobile}
        disableSubmitBtn={disableMobileMoneySubmit}
      />
    </div>
  );
});

export default ManageMerchantContent;
